import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import {
  ArrowRight, MapPin, Cpu, FileDown, Sun, Star, Zap, Battery,
  PiggyBank, TrendingDown, Home, ShieldCheck, PlugZap, Activity, Leaf,
} from "lucide-react";
import { useCompanyInfo } from "@/lib/company";
import { api } from "@/lib/api";
import SocialProofBanner from "@/components/SocialProofBanner";
import RecentTestimonialCard from "@/components/RecentTestimonialCard";

const DEFAULT_FEATURE_ICONS = [MapPin, Cpu, FileDown];

function pick(obj, lang, fallback) {
  if (!obj) return fallback;
  return obj[lang] || obj.nl || obj.fr || obj.en || fallback;
}

export const Landing = () => {
  const { t, i18n } = useTranslation();
  const company = useCompanyInfo() || {};
  const lang = (i18n.resolvedLanguage || i18n.language || "nl").split("-")[0];

  // V2.7.3 — A/B variant resolver for the "Ready for 2027" section.
  // Priority: ?variant=A|B (query) → localStorage.landing_variant → random 50/50.
  // Query param wins & is written back to localStorage so the visitor sticks
  // with a campaign-forced variant across navigations.
  const variant = useMemo(() => {
    if (typeof window === "undefined") return "A";
    try {
      const q = new URLSearchParams(window.location.search).get("variant");
      const forced = q === "A" || q === "B" ? q : null;
      if (forced) {
        window.localStorage.setItem("landing_variant", forced);
        return forced;
      }
      const stored = window.localStorage.getItem("landing_variant");
      if (stored === "A" || stored === "B") return stored;
      const rolled = Math.random() < 0.5 ? "A" : "B";
      window.localStorage.setItem("landing_variant", rolled);
      return rolled;
    } catch (_e) {
      return "A";
    }
  }, []);

  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/public-stats")
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => { /* silent — hero works fine without stats */ });
    return () => { cancelled = true; };
  }, []);

  const nfmt = new Intl.NumberFormat(lang === "en" ? "en-GB" : lang === "fr" ? "fr-FR" : "nl-NL");
  const showBadge = !!stats && (stats.quotes_total >= 10 || stats.review_rating);

  // JSON-LD LocalBusiness with AggregateRating — only when admin-set review is present
  // (Google penalizes review-stuffing on empty data, so both rating AND count must be set).
  const jsonLd = (() => {
    const hasReview =
      typeof stats?.review_rating === "number" &&
      typeof stats?.review_count === "number" &&
      stats.review_count > 0;
    if (!hasReview) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const addr = company?.address || {};
    const address = {
      "@type": "PostalAddress",
      streetAddress: addr.street || undefined,
      postalCode: addr.postal_code || undefined,
      addressLocality: addr.city || undefined,
      addressCountry: addr.country || "NL",
    };
    const sameAs = [
      company?.socials?.facebook,
      company?.socials?.instagram,
      company?.socials?.linkedin,
    ].filter(Boolean);
    const raw = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${origin}/#business`,
      name: company?.legal_name || company?.trade_name || "SolarSim",
      url: origin || undefined,
      logo: origin ? `${origin}/logo-solarsim-full.png` : undefined,
      image: origin ? `${origin}/hero-solar-trio.jpg` : undefined,
      description: company?.tagline || "Solar panel installation in the Benelux",
      priceRange: "€€",
      address,
      telephone: company?.phones?.[0] || undefined,
      email: company?.email || undefined,
      vatID: company?.vat || undefined,
      identifier: company?.kvk ? `KVK ${company.kvk}` : undefined,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: stats.review_rating,
        reviewCount: stats.review_count,
        bestRating: 5,
        worstRating: 1,
      },
      areaServed: [
        { "@type": "Country", name: "Netherlands" },
        { "@type": "Country", name: "Belgium" },
        { "@type": "Country", name: "Luxembourg" },
      ],
      sameAs: sameAs.length ? sameAs : undefined,
    };
    // Strip undefined & empty sub-objects (PostalAddress) for a clean payload
    const clean = (o) => {
      if (Array.isArray(o)) return o.map(clean).filter((v) => v !== undefined);
      if (o && typeof o === "object") {
        const out = {};
        for (const [k, v] of Object.entries(o)) {
          const cv = clean(v);
          if (cv !== undefined && !(typeof cv === "object" && !Array.isArray(cv) && Object.keys(cv).length === 0)) {
            out[k] = cv;
          }
        }
        return out;
      }
      return o === undefined ? undefined : o;
    };
    return clean(raw);
  })();

  const heroTitle = pick(company.hero_headline, lang, null);
  const heroSubtitle = pick(company.hero_subheadline, lang, null);

  const cmsFeatures = Array.isArray(company.landing_features) && company.landing_features.length === 3
    ? company.landing_features
    : null;

  const features = cmsFeatures
    ? cmsFeatures.map((f, i) => {
        const IconComp = (f?.icon && Icons[f.icon]) || DEFAULT_FEATURE_ICONS[i];
        return {
          icon: IconComp,
          title: pick(f?.title, lang, t(`landing.f${i + 1}_title`)),
          description: pick(f?.description, lang, t(`landing.f${i + 1}_desc`)),
        };
      })
    : [
        { icon: MapPin, title: t("landing.f1_title"), description: t("landing.f1_desc") },
        { icon: Cpu, title: t("landing.f2_title"), description: t("landing.f2_desc") },
        { icon: FileDown, title: t("landing.f3_title"), description: t("landing.f3_desc") },
      ];

  return (
    <div data-testid="landing-page" className="min-h-[calc(100vh-4rem)]">
      {jsonLd && (
        <script
          type="application/ld+json"
          data-testid="landing-jsonld"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      {/* V2.10 — Social proof banner (above hero). */}
      <SocialProofBanner />
      {/* V2.12 — Recent testimonial card (between banner and hero). */}
      <div className="mx-auto w-full max-w-6xl px-4 pt-4">
        <RecentTestimonialCard />
      </div>
      <section className="hero-bg relative overflow-hidden">
        <img
          src="/hero-solar-trio.jpg"
          srcSet="/hero-solar-trio-sm.jpg 960w, /hero-solar-trio.jpg 1920w"
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          data-testid="hero-bg-image"
          className="pointer-events-none absolute inset-0 w-full h-full object-cover object-center opacity-20 select-none"
        />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-solar-green/30 bg-white/70 px-3 py-1 text-xs font-medium text-solar-green backdrop-blur animate-fade-in-up"
            >
              <Sun className="h-3.5 w-3.5" />
              {t("landing.badge")}
            </div>

            <h1
              data-testid="hero-title"
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-solar-slate animate-fade-in-up"
              style={{ animationDelay: "80ms" }}
            >
              {heroTitle ? (
                heroTitle
              ) : (
                <>
                  {t("landing.title_prefix")}{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10 text-solar-green">{t("landing.title_highlight")}</span>
                    <span aria-hidden className="absolute inset-x-0 bottom-1 h-3 -z-0 bg-solar-green/15 rounded-sm" />
                  </span>{" "}
                  {t("landing.title_suffix")}
                </>
              )}
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed animate-fade-in-up"
              style={{ animationDelay: "160ms" }}
            >
              {heroSubtitle || t("landing.subtitle")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-in-up" style={{ animationDelay: "240ms" }}>
              <Link
                to="/simulateur"
                data-testid="cta-start-simulation"
                className="group inline-flex items-center gap-2 rounded-full bg-solar-green px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-solar-green/25 transition-all hover:bg-solar-green-dark hover:shadow-solar-green/40 hover:-translate-y-0.5"
              >
                {t("landing.cta")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="text-xs text-slate-500">{t("landing.no_signup")}</span>
            </div>

            {showBadge && (
              <div
                data-testid="landing-social-proof"
                className="mt-6 flex flex-wrap items-center gap-2 animate-fade-in-up"
                style={{ animationDelay: "320ms" }}
              >
                {stats.review_rating != null && (
                  <span
                    data-testid="social-proof-review"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur"
                  >
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" strokeWidth={0} />
                    <strong className="font-semibold text-solar-slate">{stats.review_rating.toFixed(1)}</strong>
                    {stats.review_count != null && (
                      <span className="text-slate-500">· {nfmt.format(stats.review_count)} {t("landing.social_proof.reviews")}</span>
                    )}
                    {stats.review_source && (
                      <span className="text-slate-400">· {stats.review_source}</span>
                    )}
                  </span>
                )}
                {stats.quotes_total >= 10 && (
                  <span
                    data-testid="social-proof-quotes"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur"
                  >
                    <strong className="font-semibold text-solar-slate">{nfmt.format(stats.quotes_total)}</strong>
                    <span>{t("landing.social_proof.quotes_generated")}</span>
                  </span>
                )}
                {stats.installations_delivered >= 5 && (
                  <span
                    data-testid="social-proof-installations"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur"
                  >
                    <strong className="font-semibold text-solar-slate">{nfmt.format(stats.installations_delivered)}</strong>
                    <span>{t("landing.social_proof.installations")}</span>
                  </span>
                )}
                {stats.review_rating != null && (
                  <Link
                    to="/reviews"
                    data-testid="landing-see-all-reviews"
                    className="inline-flex items-center gap-1 text-xs font-medium text-solar-green hover:text-solar-green-dark hover:underline underline-offset-2"
                  >
                    {t("landing.social_proof.see_all_reviews")}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        <div aria-hidden className="pointer-events-none absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-gradient-to-tr from-solar-green/10 to-solar-sky/10 blur-3xl" />
      </section>

      <section data-testid="features-section" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-solar-sky">
            {t("landing.features_kicker")}
          </h2>
          <p className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-solar-slate">
            {t("landing.features_title")}
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title || i}
                data-testid={`feature-card-${i}`}
                className="group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg hover:border-solar-green/40"
              >
                <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-solar-green/10 text-solar-green transition-colors group-hover:bg-solar-green group-hover:text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </span>
                <h3 className="mt-5 text-lg font-semibold tracking-tight text-solar-slate">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* V2.7.3 — Backup 2027 ribbon (A/B test) */}
      <BackupReady2027Section variant={variant} />

    </div>
  );
};

export default Landing;

// -------------------------------------------------------------------------
// V2.7.3 — Ready for 2027 A/B section
// -------------------------------------------------------------------------
const VARIANT_A_TILES = [
  { icon: PiggyBank, k: "landing.backup_ab.a.tile_kwh_stored" },
  { icon: TrendingDown, k: "landing.backup_ab.a.tile_roi_speed" },
  { icon: FileDown, k: "landing.backup_ab.a.tile_zero_bill" },
  { icon: Home, k: "landing.backup_ab.a.tile_resale_plus" },
];

const VARIANT_B_TILES = [
  { icon: ShieldCheck, k: "landing.backup_ab.b.tile_outages" },
  { icon: PlugZap, k: "landing.backup_ab.b.tile_self_conso" },
  { icon: Activity, k: "landing.backup_ab.b.tile_realtime" },
  { icon: Leaf, k: "landing.backup_ab.b.tile_eco" },
];

function BackupReady2027Section({ variant }) {
  const { t } = useTranslation();
  const isA = variant === "A";
  const cfg = isA
    ? {
        testId: "landing-backup-2027-variant-a",
        wrapperClass:
          "relative overflow-hidden border-y border-blue-100 bg-gradient-to-br from-blue-50 via-white to-emerald-50 py-16 lg:py-20",
        pillClass:
          "inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-blue-700 shadow-sm",
        pillIcon: PiggyBank,
        primaryCtaClass:
          "inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-transform hover:-translate-y-0.5 hover:bg-blue-700",
        primaryCtaIcon: PiggyBank,
        primaryHref: "/simulateur?utm_variant=A&utm_content=economies",
        secondaryHref: "/catalogue?tab=battery&backup=1&utm_variant=A",
        tileIconClass: "inline-grid h-9 w-9 place-items-center rounded-xl bg-blue-100 text-blue-700",
        kicker: t("landing.backup_ab.a.kicker"),
        title: t("landing.backup_ab.a.title"),
        titleHighlight: t("landing.backup_ab.a.title_highlight"),
        body: t("landing.backup_ab.a.body"),
        ctaPrimary: t("landing.backup_ab.a.cta_primary"),
        ctaSecondary: t("landing.backup_ab.a.cta_secondary"),
        tiles: VARIANT_A_TILES,
      }
    : {
        testId: "landing-backup-2027-variant-b",
        wrapperClass:
          "relative overflow-hidden border-y border-amber-100 bg-gradient-to-br from-amber-50 via-white to-orange-50 py-16 lg:py-20",
        pillClass:
          "inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-700 shadow-sm",
        pillIcon: ShieldCheck,
        primaryCtaClass:
          "inline-flex items-center gap-2 rounded-full bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-600/25 transition-transform hover:-translate-y-0.5 hover:bg-amber-700",
        primaryCtaIcon: ShieldCheck,
        primaryHref: "/simulateur?utm_variant=B&utm_content=autonomie",
        secondaryHref: "/catalogue?tab=battery&backup=1&utm_variant=B",
        tileIconClass: "inline-grid h-9 w-9 place-items-center rounded-xl bg-amber-100 text-amber-700",
        kicker: t("landing.backup_ab.b.kicker"),
        title: t("landing.backup_ab.b.title"),
        titleHighlight: t("landing.backup_ab.b.title_highlight"),
        body: t("landing.backup_ab.b.body"),
        ctaPrimary: t("landing.backup_ab.b.cta_primary"),
        ctaSecondary: t("landing.backup_ab.b.cta_secondary"),
        tiles: VARIANT_B_TILES,
      };
  const PillIcon = cfg.pillIcon;
  const CtaIcon = cfg.primaryCtaIcon;

  return (
    <section
      data-testid={cfg.testId}
      data-variant={variant}
      className={cfg.wrapperClass}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <div className={cfg.pillClass}>
              <PillIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
              {cfg.kicker}
            </div>
            <h2
              className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-solar-slate"
              data-testid="landing-backup-2027-title"
            >
              <TitleWithHighlight text={cfg.title} highlight={cfg.titleHighlight} isA={isA} />
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">
              {cfg.body}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={cfg.primaryHref}
                data-testid="landing-backup-2027-cta-primary"
                className={cfg.primaryCtaClass}
              >
                <CtaIcon className="h-4 w-4" strokeWidth={2.5} />
                {cfg.ctaPrimary}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={cfg.secondaryHref}
                data-testid="landing-backup-2027-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-solar-slate hover:border-solar-slate"
              >
                {cfg.ctaSecondary}
              </Link>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {cfg.tiles.map(({ icon: Ic, k }) => (
              <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <span className={cfg.tileIconClass}>
                  <Ic className="h-4 w-4" strokeWidth={2.2} />
                </span>
                <p className="mt-3 text-sm font-medium text-solar-slate">{t(k)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TitleWithHighlight({ text, highlight, isA }) {
  if (!highlight || !text.includes(highlight)) return <>{text}</>;
  const [before, ...rest] = text.split(highlight);
  const after = rest.join(highlight);
  const cls = isA ? "text-blue-600" : "text-amber-600";
  return (
    <>
      {before}
      <span className={cls}>{highlight}</span>
      {after}
    </>
  );
}
